import {
  EXPORT_SCHEMA_VERSION,
  ExportDataSchema,
  ExportManifestSchema,
  type ExportData,
  type ExportManifest,
} from '@hoedokloop/contracts';
import { unzipSync, zipSync } from 'fflate';

export type ExportArchiveSource = {
  appVersion: string;
  exportedAt: string;
  data: ExportData;
  photoFiles: Readonly<Record<string, Uint8Array>>;
};

export type ParsedExportArchive = {
  manifest: ExportManifest;
  data: ExportData;
  photoFiles: Record<string, Uint8Array>;
};

export type ArchiveLimits = {
  maxEntries: number;
  maxCompressedBytes: number;
  maxUncompressedBytes: number;
  maxSingleFileBytes: number;
  maxCompressionRatio: number;
};

export const DEFAULT_ARCHIVE_LIMITS: ArchiveLimits = {
  maxEntries: 2_000,
  maxCompressedBytes: 55 * 1024 * 1024,
  maxUncompressedBytes: 60 * 1024 * 1024,
  maxSingleFileBytes: 50 * 1024 * 1024,
  maxCompressionRatio: 100,
};

type CentralEntry = { path: string; compressed: number; uncompressed: number };

function safePath(path: string): boolean {
  if (!path || path.length > 240 || path.includes('\\') || path.includes('\0')) return false;
  if (path.startsWith('/') || /^[a-z]:/i.test(path)) return false;
  const segments = path.split('/');
  return segments.every((segment) => segment.length > 0 && segment !== '.' && segment !== '..');
}

function findEndOfCentralDirectory(bytes: Uint8Array): number {
  const minimum = Math.max(0, bytes.byteLength - 65_557);
  for (let index = bytes.byteLength - 22; index >= minimum; index -= 1) {
    if (
      bytes[index] === 0x50 &&
      bytes[index + 1] === 0x4b &&
      bytes[index + 2] === 0x05 &&
      bytes[index + 3] === 0x06
    ) {
      return index;
    }
  }
  throw new Error('ZIP end-of-central-directory not found');
}

function inspectCentralDirectory(bytes: Uint8Array, limits: ArchiveLimits): CentralEntry[] {
  if (bytes.byteLength > limits.maxCompressedBytes)
    throw new Error('Archive exceeds compressed limit');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const end = findEndOfCentralDirectory(bytes);
  const entries = view.getUint16(end + 10, true);
  const centralSize = view.getUint32(end + 12, true);
  const centralOffset = view.getUint32(end + 16, true);
  if (entries > limits.maxEntries) throw new Error('Archive has too many entries');
  if (centralOffset + centralSize > bytes.byteLength)
    throw new Error('Invalid central directory bounds');

  const decoder = new TextDecoder('utf-8', { fatal: true });
  const paths = new Set<string>();
  const result: CentralEntry[] = [];
  let totalUncompressed = 0;
  let cursor = centralOffset;
  for (let index = 0; index < entries; index += 1) {
    if (view.getUint32(cursor, true) !== 0x02014b50) throw new Error('Invalid central file header');
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const compressed = view.getUint32(cursor + 20, true);
    const uncompressed = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const externalAttributes = view.getUint32(cursor + 38, true);
    const next = cursor + 46 + nameLength + extraLength + commentLength;
    if (
      next > centralOffset + centralSize ||
      compressed === 0xffffffff ||
      uncompressed === 0xffffffff
    ) {
      throw new Error('ZIP64 or invalid central entry is unsupported');
    }
    const path = decoder.decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
    const unixMode = externalAttributes >>> 16;
    if ((flags & 1) !== 0) throw new Error('Encrypted ZIP entries are unsupported');
    if (method !== 0 && method !== 8) throw new Error('Unsupported ZIP compression method');
    if ((unixMode & 0o170000) === 0o120000) throw new Error('Symbolic links are forbidden');
    if (!safePath(path) || paths.has(path)) throw new Error('Unsafe or duplicate archive path');
    if (uncompressed > limits.maxSingleFileBytes)
      throw new Error('Archive entry exceeds size limit');
    if (
      uncompressed > 1024 * 1024 &&
      uncompressed / Math.max(1, compressed) > limits.maxCompressionRatio
    ) {
      throw new Error('Archive entry exceeds compression ratio limit');
    }
    paths.add(path);
    totalUncompressed += uncompressed;
    if (totalUncompressed > limits.maxUncompressedBytes) {
      throw new Error('Archive exceeds uncompressed limit');
    }
    result.push({ path, compressed, uncompressed });
    cursor = next;
  }
  if (cursor !== centralOffset + centralSize) throw new Error('Central directory size mismatch');
  return result;
}

async function sha256(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

function encodeJson(value: unknown): Uint8Array {
  return new TextEncoder().encode(`${JSON.stringify(value)}\n`);
}

function decodeJson(bytes: Uint8Array): unknown {
  return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
}

function entityCounts(data: ExportData): Record<string, number> {
  return {
    exams: data.exams.length,
    subjects: data.subjects.length,
    studyLogs: data.studyLogs.length,
    wrongNotes: data.wrongNotes.length,
    photoAssets: data.photoAssets.length,
    plans: data.plans.length,
    planItems: data.planItems.length,
    milestones: data.milestones.length,
    mogakSessions: data.mogakSessions.length,
    ...(data.schemaVersion === 1
      ? {}
      : {
          mockScores: (data.mockScores ?? []).length,
          noteInbox: (data.noteInbox ?? []).length,
          userSettings: (data.userSettings ?? []).length,
        }),
  };
}

function verifyReferences(data: ExportData): void {
  const exams = new Set(data.exams.map((item) => item.id));
  const subjects = new Map(data.subjects.map((item) => [item.id, item]));
  const photos = new Set(data.photoAssets.map((item) => item.id));
  const plans = new Map(data.plans.map((item) => [item.id, item]));
  for (const subject of data.subjects) {
    if (!exams.has(subject.examId)) throw new Error('Subject references a missing exam');
  }
  for (const log of data.studyLogs) {
    const subject = subjects.get(log.subjectId);
    if (!subject || subject.examId !== log.examId) throw new Error('Study log reference mismatch');
  }
  for (const note of data.wrongNotes) {
    const subject = subjects.get(note.subjectId);
    if (!subject || subject.examId !== note.examId)
      throw new Error('Wrong note reference mismatch');
    if (note.photoAssetId && !photos.has(note.photoAssetId))
      throw new Error('Missing photo reference');
  }
  for (const plan of data.plans)
    if (!exams.has(plan.examId)) throw new Error('Plan references missing exam');
  for (const item of data.planItems) {
    const plan = plans.get(item.planId);
    const subject = subjects.get(item.subjectId);
    if (!plan || !subject || plan.examId !== subject.examId)
      throw new Error('Plan item reference mismatch');
  }
  for (const milestone of data.milestones) {
    if (milestone.examId && !exams.has(milestone.examId)) {
      throw new Error('Milestone references missing exam');
    }
  }
  for (const score of data.mockScores ?? []) {
    if (
      !exams.has(score.examId) ||
      score.scores.some((item) => subjects.get(item.subjectId)?.examId !== score.examId)
    )
      throw new Error('Score reference mismatch');
  }
  for (const item of data.noteInbox ?? []) {
    if (!photos.has(item.photoAssetId)) throw new Error('Inbox photo reference mismatch');
  }
  for (const session of data.mogakSessions) {
    if (!exams.has(session.examId)) throw new Error('Room session references missing exam');
  }
}

export async function createExportArchive(source: ExportArchiveSource): Promise<Uint8Array> {
  const data = ExportDataSchema.parse(source.data);
  if (![1, 2, EXPORT_SCHEMA_VERSION].includes(data.schemaVersion))
    throw new Error('Unsupported export schema');
  const files: Record<string, Uint8Array> = { 'data.json': encodeJson(data) };
  for (const [path, bytes] of Object.entries(source.photoFiles)) {
    if (!safePath(path) || !path.startsWith('photos/')) throw new Error('Unsafe photo path');
    files[path] = bytes;
  }
  for (const photo of data.photoAssets) {
    if (!files[photo.originalPath] || !files[photo.thumbnailPath]) {
      throw new Error('Photo manifest file is missing');
    }
  }
  const manifestFiles: ExportManifest['files'] = {};
  for (const [path, bytes] of Object.entries(files)) {
    manifestFiles[path] = { sha256: await sha256(bytes), byteLength: bytes.byteLength };
  }
  for (const photo of data.photoAssets) {
    if (manifestFiles[photo.originalPath]?.sha256 !== photo.sha256) {
      throw new Error('Photo content hash does not match photo metadata');
    }
  }
  const manifest = ExportManifestSchema.parse({
    schemaVersion: data.schemaVersion,
    exportedAt: source.exportedAt,
    appVersion: source.appVersion,
    counts: entityCounts(data),
    files: manifestFiles,
  });
  return zipSync({ 'manifest.json': encodeJson(manifest), ...files }, { level: 6 });
}

export async function readExportArchive(
  bytes: Uint8Array,
  limits: ArchiveLimits = DEFAULT_ARCHIVE_LIMITS,
): Promise<ParsedExportArchive> {
  const inspected = inspectCentralDirectory(bytes, limits);
  const unpacked = unzipSync(bytes);
  if (Object.keys(unpacked).length !== inspected.length)
    throw new Error('Archive entry count mismatch');
  const manifestBytes = unpacked['manifest.json'];
  const dataBytes = unpacked['data.json'];
  if (!manifestBytes || !dataBytes) throw new Error('Archive is missing required files');
  const manifest = ExportManifestSchema.parse(decodeJson(manifestBytes));
  const data = ExportDataSchema.parse(decodeJson(dataBytes));

  const archivePaths = new Set(Object.keys(unpacked).filter((path) => path !== 'manifest.json'));
  const declaredPaths = new Set(Object.keys(manifest.files));
  if (
    archivePaths.size !== declaredPaths.size ||
    [...archivePaths].some((path) => !declaredPaths.has(path))
  ) {
    throw new Error('Archive files do not match manifest');
  }
  for (const [path, expected] of Object.entries(manifest.files)) {
    const file = unpacked[path];
    if (
      !file ||
      file.byteLength !== expected.byteLength ||
      (await sha256(file)) !== expected.sha256
    ) {
      throw new Error(`Archive hash mismatch: ${path}`);
    }
  }
  const expectedCounts = entityCounts(data);
  for (const [name, count] of Object.entries(expectedCounts)) {
    if (manifest.counts[name] !== count) throw new Error(`Archive count mismatch: ${name}`);
  }
  verifyReferences(data);
  const photoFiles = Object.fromEntries(
    Object.entries(unpacked).filter(([path]) => path.startsWith('photos/')),
  );
  for (const photo of data.photoAssets) {
    if (!photoFiles[photo.originalPath] || !photoFiles[photo.thumbnailPath]) {
      throw new Error('Photo files are missing after extraction');
    }
    if (manifest.files[photo.originalPath]?.sha256 !== photo.sha256) {
      throw new Error('Photo metadata hash mismatch');
    }
  }
  return { manifest, data, photoFiles };
}
